import _ from 'lodash'

import {brandMonitoringPlatforms} from '@/content/brand-monitoring/constants'
import {Case_Solution} from '@/utils/graphqlSdk'
import {ServiceType} from '@/utils/service/constants'

import {BrandSubscriptionPlatform} from '../brand-subscription/constants'
import {Jurisdiction} from '../countries/constants'

export enum CaseSolutionStatus {
  available = 'Available', // can be seen and ordered by client
  ordered = 'Ordered', // ordered by client
  inactive = 'Inactive', // deactivated when one solution was ordered
  hidden = 'Hidden', // hidden by admin in case edit
}

export enum CaseSolutionItemType {
  takedown = 'Takedown',
  opposition = 'Opposition',
  warningLetter = 'Warning letter',
  ceaseDesistLetter = 'Cease & desist letter',
  negotiations = 'Negotiations',
  domainDispute = 'Domain dispute',
  cancellation = 'Cancellation',
  legalConsultation = 'Legal consultation',
  usLetterOfProtest = 'US Letter of protest',
}

export const subscriptionWithEnforcementFreeItemTypes = [
  CaseSolutionItemType.takedown,
  CaseSolutionItemType.warningLetter,
  CaseSolutionItemType.ceaseDesistLetter,
]

export const solutionTypeToServiceTypeMapping: Record<CaseSolutionItemType, ServiceType> = {
  [CaseSolutionItemType.takedown]: ServiceType.takedown,
  [CaseSolutionItemType.opposition]: ServiceType.opposition,
  [CaseSolutionItemType.warningLetter]: ServiceType.warningLetter,
  [CaseSolutionItemType.ceaseDesistLetter]: ServiceType.ceaseAndDesist,
  [CaseSolutionItemType.negotiations]: ServiceType.negotiations,
  [CaseSolutionItemType.domainDispute]: ServiceType.domainDispute,
  [CaseSolutionItemType.cancellation]: ServiceType.cancellation,
  [CaseSolutionItemType.legalConsultation]: ServiceType.legalConsultation,
  [CaseSolutionItemType.usLetterOfProtest]: ServiceType.usLetterOfProtest,
}

export const serviceTypeToSolutionTypeMapping: Record<ServiceType, CaseSolutionItemType> = _.invert(
  solutionTypeToServiceTypeMapping,
) as Record<ServiceType, CaseSolutionItemType>

export const caseSolutionPlatforms = _.pick(brandMonitoringPlatforms, [
  BrandSubscriptionPlatform.amazon,
  BrandSubscriptionPlatform.googleAds,
  BrandSubscriptionPlatform.facebook,
  BrandSubscriptionPlatform.instagram,
  BrandSubscriptionPlatform.ebay,
  BrandSubscriptionPlatform.appStore,
  BrandSubscriptionPlatform.googlePlay,
  BrandSubscriptionPlatform.aliExpress,
  BrandSubscriptionPlatform.youtube,
  BrandSubscriptionPlatform.etsy,
  BrandSubscriptionPlatform.tiktok,
  BrandSubscriptionPlatform.temu,
  BrandSubscriptionPlatform.fruugo,
  BrandSubscriptionPlatform.shopify,
  BrandSubscriptionPlatform.walmart,
])

export const caseSolutionOppositionJurisdictions = [
  Jurisdiction.US,
  Jurisdiction.UK,
  Jurisdiction.EU,
  Jurisdiction.DE,
  Jurisdiction.ES,
  Jurisdiction.IT,
  Jurisdiction.FR,
]

export const caseSolutionCancellationJurisdictions = [
  Jurisdiction.US,
  Jurisdiction.UK,
  Jurisdiction.EU,
]

export const getCaseSolutionItemJurisdictions: (type: CaseSolutionItemType) => Jurisdiction[] = (
  type,
) => {
  switch (type) {
    case CaseSolutionItemType.opposition:
      return caseSolutionOppositionJurisdictions
    case CaseSolutionItemType.cancellation:
      return caseSolutionCancellationJurisdictions
    default:
      return []
  }
}

// caseSolutionItemAdditionalInfo[CaseSolutionType] = <field>[] - which fields are required for the type
export const caseSolutionItemAdditionalInfo: Partial<Record<CaseSolutionItemType, string[]>> = {
  [CaseSolutionItemType.takedown]: ['platform'],
  [CaseSolutionItemType.opposition]: ['jurisdiction', 'option', 'deadline'],
  [CaseSolutionItemType.ceaseDesistLetter]: ['option'],
  [CaseSolutionItemType.domainDispute]: ['option'],
  [CaseSolutionItemType.cancellation]: ['jurisdiction', 'option'],
}

// caseSolutionItemAdditionalFields[<field>] = CaseSolutionType[] - for which types the field is required;
// inverse of caseSolutionItemAdditionalInfo
export const caseSolutionItemAdditionalFields = _.mapValues(
  _.groupBy(
    Object.entries(caseSolutionItemAdditionalInfo)
      .map(([type, fields]) =>
        fields.map((field) => {
          return {field, type: type as CaseSolutionItemType}
        }),
      )
      .flat(),
    'field',
  ),
  (a) => a.map(({type}) => type),
)

export const caseSolutionEditableProps: Array<keyof Case_Solution> = [
  'items',
  'currency',
  'offer_subscription',
  'offer_watchlist',
]

const caseSolutionOppositionDefaultOption = {
  ['default']: 'Opposition',
}

const caseSolutionItemOptions = {
  [CaseSolutionItemType.opposition]: {
    [Jurisdiction.US]: {
      ...caseSolutionOppositionDefaultOption,
      ['extension-request']: 'Extension request',
    },
    [Jurisdiction.UK]: {
      ['threatened']: 'Notice of threatened opposition',
      ['with-reasoning']: 'With reasoning',
      ['without-reasoning']: 'Without reasoning',
      ['more-grounds']: 'More grounds',
      ['fasttrack']: 'Fast track',
      ['substantive-action']: 'Substantive action',
    },
    [Jurisdiction.EU]: {
      ['with-reasoning']: 'With reasoning',
      ['without-reasoning']: 'Without reasoning',
      ['substantive-action']: 'Substantive action',
    },
    [Jurisdiction.DE]: caseSolutionOppositionDefaultOption,
    [Jurisdiction.ES]: caseSolutionOppositionDefaultOption,
    [Jurisdiction.IT]: caseSolutionOppositionDefaultOption,
    [Jurisdiction.FR]: caseSolutionOppositionDefaultOption,
  },
  [CaseSolutionItemType.ceaseDesistLetter]: {
    ['simple']: 'Simple',
    ['package']: 'Negotiations package',
  },
  [CaseSolutionItemType.domainDispute]: {
    ['resolution']: 'Resolution',
    ['observations']: 'Observations',
  },
  [CaseSolutionItemType.cancellation]: {
    [Jurisdiction.US]: {},
    [Jurisdiction.UK]: {
      ['with-reasoning']: 'Cancellation',
      ['substantive-action']: 'Substantive action',
    },
    [Jurisdiction.EU]: {
      ['with-reasoning']: 'With reasoning',
      ['request-without-reasoning']: 'Without reasoning',
      ['substantive-action']: 'Substantive action',
    },
  },
}

export const getCaseSolutionItemOptions: (
  type: CaseSolutionItemType,
  jurisdiction?: Jurisdiction,
) => Record<string, any> = (type, jurisdiction) => {
  const parameters = _.compact([
    type,
    caseSolutionItemAdditionalFields['jurisdiction'].includes(type) ? jurisdiction : undefined,
  ])
  return _.pickBy(
    _.get(caseSolutionItemOptions, parameters, {}),
    (value) => typeof value === 'string',
  )
}
